/**
 * 权限相关的组合式函数
 * 提供在组件中使用权限功能的便捷方法
 */

import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores'
import { 
  permissionManager, 
  Permission, 
  UserType, 
  type MenuItem,
  type UserInfo
} from '@/utils/permission'
import { PermissionCode, PermissionGroup } from '@/constants/permission-codes'

/**
 * 权限检查组合式函数
 */
export function usePermission() {
  const userStore = useUserStore()
  const router = useRouter()

  // 当前用户信息
  const currentUser = computed(() => userStore.userInfo)
  
  // 当前用户类型
  const currentUserType = computed(() => currentUser.value?.user_type)
  
  // 是否为管理员
  const isAdmin = computed(() => permissionManager.isAdmin())

  // 监听用户信息变化，更新权限管理器
  watch(currentUser, (user) => {
    if (user) {
      permissionManager.setUser(user)
    } else {
      permissionManager.clearUser()
    }
  }, { immediate: true })

  /**
   * 检查是否有指定权限
   */
  const hasPermission = (permission: Permission): boolean => {
    return permissionManager.hasPermission(permission)
  }

  /**
   * 检查是否有任一权限
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissionManager.hasAnyPermission(permissions)
  }

  /**
   * 检查是否有所有权限
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissionManager.hasAllPermissions(permissions)
  }

  /**
   * 检查是否有指定角色
   */
  const hasRole = (role: UserType): boolean => {
    return permissionManager.hasRole(role)
  }

  /**
   * 检查是否有任一角色
   */
  const hasAnyRole = (roles: UserType[]): boolean => {
    return permissionManager.hasAnyRole(roles)
  }

  /**
   * 检查路由权限
   */
  const canAccessRoute = (routePath: string): boolean => {
    const route = router.getRoutes().find(r => r.path === routePath)
    return route ? permissionManager.checkRoutePermission(route) : false
  }

  /**
   * 跳转到指定路由（带权限检查）
   */
  const navigateWithPermissionCheck = (routePath: string) => {
    if (canAccessRoute(routePath)) {
      router.push(routePath)
    } else {
      router.push('/403')
    }
  }

  return {
    currentUser,
    currentUserType,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    canAccessRoute,
    navigateWithPermissionCheck
  }
}

/**
 * 菜单权限过滤组合式函数
 */
export function useMenuPermission() {
  const { currentUser } = usePermission()

  /**
   * 过滤菜单项
   */
  const filterMenus = (menus: MenuItem[]): MenuItem[] => {
    return permissionManager.filterMenuByPermission(menus)
  }

  /**
   * 检查菜单项是否可见
   */
  const isMenuVisible = (menu: MenuItem): boolean => {
    if (!currentUser.value) return false

    if (menu.meta?.permissions && !permissionManager.hasAnyPermission(menu.meta.permissions)) {
      return false
    }

    // 检查是否隐藏
    if (menu.meta?.hidden) {
      return false
    }

    return true
  }

  return {
    filterMenus,
    isMenuVisible
  }
}

/**
 * 动态权限组合式函数
 * 用于需要动态检查权限的场景
 */
export function useDynamicPermission() {
  const permissionCache = ref(new Map<string, boolean>())

  /**
   * 缓存权限检查结果
   */
  const checkAndCachePermission = (key: string, permission: Permission): boolean => {
    if (permissionCache.value.has(key)) {
      return permissionCache.value.get(key)!
    }

    const result = permissionManager.hasPermission(permission)
    permissionCache.value.set(key, result)
    return result
  }

  /**
   * 清除权限缓存
   */
  const clearPermissionCache = () => {
    permissionCache.value.clear()
  }

  /**
   * 批量检查权限
   */
  const batchCheckPermissions = (permissions: Permission[]): Record<string, boolean> => {
    const results: Record<string, boolean> = {}
    permissions.forEach(permission => {
      results[permission] = permissionManager.hasPermission(permission)
    })
    return results
  }

  return {
    checkAndCachePermission,
    clearPermissionCache,
    batchCheckPermissions
  }
}

/**
 * 权限状态组合式函数
 * 提供响应式的权限状态
 */
export function usePermissionState() {
  const userStore = useUserStore()
  
  // 权限状态
  const permissionState = computed(() => ({
    isLoggedIn: !!userStore.token,
    userType: userStore.userInfo?.user_type,
    permissions: permissionManager.getCurrentPermissions(),
    hasSystemAccess: permissionManager.hasAnyPermission(PermissionGroup.SYSTEM_MANAGEMENT_PERMISSIONS as Permission[]),
    canCreateProject: permissionManager.hasPermission(PermissionCode.PROJECT_CREATE),
    canManageUsers: permissionManager.hasPermission(PermissionCode.SYSTEM_USER),
    canAssignTasks: permissionManager.hasPermission(PermissionCode.TASK_ASSIGN)
  }))

  return {
    permissionState
  }
}